"use client";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface BasicAuthFormProps {
  config: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
}

type AuthMode = "credentials" | "token";

export function BasicAuthForm({ config, onChange }: BasicAuthFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showToken, setShowToken] = useState(false);

  const hasToken = Boolean(config.encoded_token);
  const hasCredentials = Boolean(config.username || config.password);
  const mode: AuthMode =
    (config._mode as AuthMode) ||
    (hasToken && !hasCredentials ? "token" : "credentials");

  const setMode = (newMode: AuthMode) => {
    if (newMode === "token") {
      onChange({ _mode: "token", encoded_token: (config.encoded_token as string) || "", username: undefined, password: undefined });
    } else {
      onChange({ _mode: "credentials", username: (config.username as string) || "", password: (config.password as string) || "", encoded_token: undefined });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setMode("credentials")}
          className={cn(
            "rounded-md px-3 py-1.5 text-xs font-medium transition-all",
            mode === "credentials"
              ? "bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/40"
              : "bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/70",
          )}
        >
          Username &amp; Password
        </button>
        <button
          type="button"
          onClick={() => setMode("token")}
          className={cn(
            "rounded-md px-3 py-1.5 text-xs font-medium transition-all",
            mode === "token"
              ? "bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/40"
              : "bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/70",
          )}
        >
          Encoded Token
        </button>
      </div>

      {mode === "credentials" ? (
        <>
          <div className="space-y-2">
            <Label className="text-white/80">Username</Label>
            <Input
              value={(config.username as string) || ""}
              onChange={(e) => onChange({ username: e.target.value })}
              placeholder="Enter username"
              className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-white/80">Password</Label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={(config.password as string) || ""}
                onChange={(e) => onChange({ password: e.target.value })}
                placeholder="Enter password"
                className="border-white/10 bg-white/5 pr-10 text-white placeholder:text-white/40"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 text-white/40 hover:text-white"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-white/40">
              Credentials will be Base64 encoded and sent as{" "}
              <code className="text-white/60">Authorization: Basic ...</code>
            </p>
          </div>
        </>
      ) : (
        <div className="space-y-2">
          <Label className="text-white/80">Basic Auth Token</Label>
          <div className="relative">
            <Input
              type={showToken ? "text" : "password"}
              value={(config.encoded_token as string) || ""}
              onChange={(e) => onChange({ encoded_token: e.target.value })}
              placeholder="Base64 encoded token (e.g. dXNlcjpwYXNz)"
              className="border-white/10 bg-white/5 pr-10 text-white placeholder:text-white/40"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 text-white/40 hover:text-white"
              onClick={() => setShowToken(!showToken)}
            >
              {showToken ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="text-xs text-white/40">
            Paste the pre-encoded token directly. The header sent will be{" "}
            <code className="text-white/60">Authorization: Basic &lt;token&gt;</code>
          </p>
        </div>
      )}
    </div>
  );
}
